import { ArrowLeft } from 'lucide-react';
import { Link } from '@tanstack/react-router';

import { getRoiProjectById } from '../data/roi-demo-dataset';
import type { RoiProject, RoiSignal, RoiSignalSource } from '../types/roi';
import { RoiVerdictChip } from './RoiVerdictChip';

interface RoiProjectDetailPageProps {
  projectId: string;
}

const SOURCE_LABELS: Record<RoiSignalSource, string> = {
  jira: 'Jira',
  linear: 'Linear',
  monday: 'monday.com',
  asana: 'Asana',
  github: 'GitHub',
  slack: 'Slack',
  teams: 'Microsoft Teams',
  confluence: 'Confluence',
  notion: 'Notion',
  sharepoint: 'SharePoint',
  gdrive: 'Google Drive',
  pagerduty: 'PagerDuty',
  salesforce: 'Salesforce',
  hubspot: 'HubSpot',
};

export function RoiProjectDetailPage({ projectId }: RoiProjectDetailPageProps) {
  const project = getRoiProjectById(projectId);

  if (!project) {
    return <ProjectNotFound />;
  }

  return (
    <div className="min-h-screen w-full bg-page-bg">
      <main className="mx-auto flex w-full max-w-[1000px] flex-col gap-12 px-6 py-12 pb-32 md:px-10">
        <Link
          to="/roi-demo"
          className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 transition-colors hover:text-slate-900"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to dashboard
        </Link>

        <header className="flex flex-col gap-5">
          <div className="flex items-center gap-3">
            <RoiVerdictChip verdict={project.verdict} />
            <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
              Hero case · {project.team}
            </span>
          </div>
          <h1 className="text-4xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-5xl">
            {project.name}
          </h1>
          <p className="max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
            {project.narrative}
          </p>
        </header>

        <ProjectStats project={project} />
        <SignalTimeline signals={project.signals} />
        <ImpactMetrics project={project} />
        <VerdictBlock project={project} />
      </main>
    </div>
  );
}

interface ProjectStatsProps {
  project: RoiProject;
}

function ProjectStats({ project }: ProjectStatsProps) {
  return (
    <section className="grid grid-cols-2 gap-x-6 gap-y-5 rounded-2xl border border-gray-200 bg-white p-7 shadow-sm md:grid-cols-4">
      <Stat label="Planned ship" value={project.plannedShip} />
      <Stat label="Actual ship" value={project.actualShip} />
      <Stat label="Headcount" value={`${project.headcount} engineers`} />
      <Stat
        label="Failure modes"
        value={project.failureModes.join(' · ') || '-'}
      />
    </section>
  );
}

interface StatProps {
  label: string;
  value: string;
}

function Stat({ label, value }: StatProps) {
  return (
    <div>
      <span className="mb-1 block text-[10px] uppercase tracking-wider text-slate-400">
        {label}
      </span>
      <span className="block text-base font-semibold tabular-nums text-slate-900">
        {value}
      </span>
    </div>
  );
}

interface SignalTimelineProps {
  signals: RoiSignal[];
}

function SignalTimeline({ signals }: SignalTimelineProps) {
  return (
    <section>
      <h2 className="mb-6 text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">
        Signals Intera would have raised
      </h2>
      <ol className="space-y-0 border-s border-gray-200 ps-6">
        {signals.map((signal, index) => (
          <li
            key={`${signal.ref}-${index}`}
            className="relative pb-7 last:pb-0"
          >
            <span className="absolute -start-[1.625rem] top-1.5 h-2.5 w-2.5 rounded-full border-2 border-[rgb(0,115,234)] bg-white" />
            <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              <span className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-[10px] text-slate-600">
                {SOURCE_LABELS[signal.source]}
              </span>
              <span className="font-mono text-[11px] text-slate-700">
                {signal.ref}
              </span>
              {typeof signal.dayOffset === 'number' && (
                <span className="text-slate-400">
                  Day {signal.dayOffset} from project start
                </span>
              )}
            </div>
            <p className="mt-2 text-sm leading-relaxed text-slate-700">
              {signal.description}
            </p>
          </li>
        ))}
      </ol>
    </section>
  );
}

interface ImpactMetricsProps {
  project: RoiProject;
}

function ImpactMetrics({ project }: ImpactMetricsProps) {
  const rows: Array<{ label: string; sub: string; value: string }> = [
    {
      label: 'Rework during development',
      sub: 'Engineer-days spent reopening, reverting, or redoing finished work.',
      value:
        project.reworkEngineerDays > 0
          ? `${project.reworkEngineerDays} engineer-days`
          : '-',
    },
    {
      label: 'Schedule slip',
      sub: 'Weeks past the planned ship date.',
      value:
        project.weeksSlipped > 0 ? `${project.weeksSlipped} weeks late` : '-',
    },
    {
      label: 'Post-launch firefighting',
      sub: 'P1 incidents + hotfix PRs in the 30 days after ship.',
      value:
        project.firefightEngineerDays > 0
          ? `${project.firefightEngineerDays} engineer-days`
          : '-',
    },
  ];

  return (
    <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      <header className="border-b border-gray-200 bg-slate-50/60 px-7 py-4">
        <h2 className="text-sm font-semibold text-slate-900">
          Project impact metrics
        </h2>
        <p className="mt-1 text-xs text-slate-500">
          Each row shows where the project leaked time. The dashboard headline
          dollars come from the org-wide rework formula, not from these
          per-project numbers.
        </p>
      </header>
      <table className="w-full border-collapse">
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.label}
              className="border-b border-gray-100 last:border-b-0"
            >
              <td className="px-7 py-4">
                <div className="text-sm font-medium text-slate-900">
                  {row.label}
                </div>
                <div className="mt-0.5 text-xs text-slate-500">{row.sub}</div>
              </td>
              <td className="px-7 py-4 text-end text-sm font-semibold tabular-nums text-slate-900">
                {row.value}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

interface VerdictBlockProps {
  project: RoiProject;
}

function VerdictBlock({ project }: VerdictBlockProps) {
  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-7 shadow-sm">
      <div className="mb-3 flex items-center gap-3">
        <RoiVerdictChip verdict={project.verdict} />
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
          Verdict
        </span>
      </div>
      <p className="text-sm leading-relaxed text-slate-700">
        {project.verdictRationale}
      </p>
    </section>
  );
}

function ProjectNotFound() {
  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-page-bg p-10">
      <div className="max-w-md text-center">
        <h1 className="mb-3 text-2xl font-bold text-slate-900">
          Project not found
        </h1>
        <p className="mb-6 text-sm text-slate-600">
          Only hero case studies have a dedicated detail page.
        </p>
        <Link
          to="/roi-demo"
          className="inline-flex items-center gap-2 rounded border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:border-slate-300"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to dashboard
        </Link>
      </div>
    </div>
  );
}
